import type { FormEvent } from "react";
import { useMemo, useState } from "react";
import type { Profile, UpdateProfilePayload } from "api";
import cn from "classnames";
import { Select } from "components/ui/Select";
import {
  COUNTRIES_LIST,
  dialCountryFor,
  findCountryByCode,
  findCountryByName,
  splitPhone,
} from "constants/countries.constants";
import { useFlash } from "hooks/useFlash";
import { createFlagIconUrl } from "utils/url.utils";

type ProfileFormProps = {
  profile: Profile;
  onSave: (payload: UpdateProfilePayload) => Promise<string | null>;
};

const flagFor = (countryCode: string) => (
  <img className="pcflag" src={createFlagIconUrl(countryCode)} alt="" width={20} height={15} loading="lazy" />
);

export const ProfileForm = ({ profile, onSave }: ProfileFormProps) => {
  const initialCountry = findCountryByCode(profile.country_code);
  // A number saved at registration still carries its dial code; the code the
  // number came with wins over the stored one, which may only be a backfill.
  const storedPhone = splitPhone(profile.phone);
  // Falls back to the country's own dial code for accounts registered before
  // the two were stored separately.
  const initialDialCode = storedPhone.dialCode || profile.dial_code || initialCountry?.iso || "";
  // The flag beside the code follows the code, not the country: the two are
  // separate fields and an account may well have Åland and +46.
  const initialDialCountry = dialCountryFor(initialDialCode, initialCountry);

  const [firstName, setFirstName] = useState(profile.first_name ?? "");
  const [lastName, setLastName] = useState(profile.last_name ?? "");
  const [company, setCompany] = useState(profile.company?.name ?? "");
  const [countryName, setCountryName] = useState(initialCountry?.name ?? "");
  const [dialCode, setDialCode] = useState(initialDialCode);
  const [dialCountryName, setDialCountryName] = useState(initialDialCountry?.name ?? "");
  const [phone, setPhone] = useState(storedPhone.number);

  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const { shown: saved, flash: flashSaved, clear: clearSaved } = useFlash();
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);

  const countryOptions = useMemo(
    () =>
      COUNTRIES_LIST.map((country) => ({
        value: country.name,
        label: country.name,
        icon: flagFor(country.countryCode),
      })),
    []
  );

  // Several countries share a dial code, so the option value has to stay the
  // country name; only the button shows the code.
  const dialOptions = useMemo(
    () =>
      COUNTRIES_LIST.map((country) => ({
        value: country.name,
        label: country.name,
        hint: country.iso,
        icon: flagFor(country.countryCode),
      })),
    []
  );

  const change =
    <T,>(setter: (value: T) => void) =>
    (value: T) => {
      setter(value);
      setDirty(true);
      clearSaved();
    };

  const firstNameMissing = touched && firstName.trim() === "";

  const selectDialCountry = (name: string) => {
    change(setDialCountryName)(name);
    setDialCode(findCountryByName(name)?.iso ?? "");
  };

  /**
   * Paste a full international number and the code moves into the select
   * instead of sitting in the input twice. Anything the API would reject is
   * dropped as it is typed.
   */
  const changePhone = (value: string) => {
    const { dialCode: pastedCode, number } = splitPhone(value);

    if (pastedCode !== "") {
      const country = dialCountryFor(pastedCode, findCountryByName(countryName));

      change(setDialCountryName)(country?.name ?? "");
      setDialCode(pastedCode);
    }

    change(setPhone)(number);
  };

  const reset = () => {
    setFirstName(profile.first_name ?? "");
    setLastName(profile.last_name ?? "");
    setCompany(profile.company?.name ?? "");
    setCountryName(initialCountry?.name ?? "");
    setDialCountryName(initialDialCountry?.name ?? "");
    setDialCode(initialDialCode);
    setPhone(storedPhone.number);
    setDirty(false);
    setTouched(false);
    setError(null);
    clearSaved();
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setTouched(true);

    if (firstName.trim() === "") {
      return;
    }

    setSaving(true);
    setError(null);

    const message = await onSave({
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      company_name: company.trim(),
      country_code: findCountryByName(countryName)?.countryCode ?? "",
      dial_code: dialCode,
      phone: phone.trim(),
    });

    setSaving(false);

    if (message === null) {
      setDirty(false);
      setError(null);
      flashSaved();
      return;
    }

    setError(message);
  };

  return (
    <form className="pcx" noValidate onSubmit={(event) => void submit(event)}>
      <div className="pcx__top">
        <div>
          <div className="pcx__h">Profile</div>
          <p className="pcx__sub">These details appear on invoices and in support requests.</p>
        </div>
      </div>

      <div className="pc-form">
        <label className={cn("pcf", { "is-bad": firstNameMissing })}>
          <span>First name</span>
          <input
            type="text"
            name="first"
            value={firstName}
            autoComplete="given-name"
            onChange={(event) => change(setFirstName)(event.target.value)}
          />
          <small className="pcf__err">Enter your first name</small>
        </label>

        <label className="pcf">
          <span>Last name</span>
          <input
            type="text"
            name="last"
            value={lastName}
            autoComplete="family-name"
            onChange={(event) => change(setLastName)(event.target.value)}
          />
        </label>

        <label className="pcf">
          <span>Email</span>
          <input type="email" name="email" value={profile.email} autoComplete="email" readOnly />
        </label>

        <label className="pcf">
          <span>Company name</span>
          <input
            type="text"
            name="company"
            value={company}
            autoComplete="organization"
            onChange={(event) => change(setCompany)(event.target.value)}
          />
        </label>

        <div className="pcf">
          <span>Country</span>
          <Select
            value={countryName}
            options={countryOptions}
            onChange={change(setCountryName)}
            ariaLabel="Country"
            placeholder="Select a country"
            searchable
          />
        </div>

        <div className="pcf">
          <span>Phone number</span>
          <div className="pcphone">
            <Select
              value={dialCountryName}
              options={dialOptions}
              onChange={selectDialCountry}
              ariaLabel="Country dial code"
              variant="code"
              buttonLabel={dialCode || "Code"}
              searchable
            />
            <input
              type="tel"
              className="pcphone__in"
              name="phone"
              value={phone}
              placeholder="(000) 00 00 000"
              autoComplete="tel"
              onChange={(event) => changePhone(event.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="pc-acts">
        <button type="submit" className="pcbtn pcbtn--main" disabled={saving}>
          {saving ? "Saving…" : "Save changes"}
        </button>
        {dirty && (
          <button type="button" className="pcbtn" onClick={reset}>
            Cancel
          </button>
        )}
        {saved && <span className="pc-saved">Saved</span>}
        {error && <span className="pc-failed">{error}</span>}
      </div>
    </form>
  );
};
